/**
 * Super-admin script library browser. Lists every producer's saved scripts and lets
 * the admin open one to read/assist-edit it offline (auto-saved back to the owner's
 * library). This is the "browse anytime" half of the plan — independent of whether
 * the producer is currently live.
 */
import { useCallback, useEffect, useState } from 'react';
import type { Project } from '@goanna-im/shared';
import {
  adminDeleteScript,
  adminSaveScript,
  getScript,
  listAllScripts,
} from '../lib/library.js';
import type { ScriptSummary, StoredScript } from '../lib/library.js';
import { ScriptTextEditor } from './ScriptTextEditor.js';
import { AiScriptPanel } from '../components/AiScriptPanel.js';

function ago(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function AdminLibrary({ onError }: { onError: (msg: string) => void }) {
  const [scripts, setScripts] = useState<ScriptSummary[]>([]);
  const [open, setOpen] = useState<StoredScript | null>(null);
  const [saving, setSaving] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [editorKey, setEditorKey] = useState(0);

  const refresh = useCallback(async () => {
    try {
      setScripts(await listAllScripts());
    } catch (e) {
      onError(String((e as Error).message));
    }
  }, [onError]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const openScript = async (id: string) => {
    try {
      const s = await getScript(id);
      setOpen(s);
      setEditorKey((k) => k + 1);
    } catch (e) {
      onError(String((e as Error).message));
    }
  };

  const save = async (project: Project) => {
    setSaving('saving');
    try {
      const saved = await adminSaveScript(project);
      setOpen((cur) => (cur ? { ...cur, ...saved } : cur));
      setSaving('saved');
      setTimeout(() => setSaving('idle'), 1200);
      void refresh();
    } catch (e) {
      setSaving('idle');
      onError(String((e as Error).message));
    }
  };

  const remove = async (s: ScriptSummary) => {
    if (!confirm(`Delete "${s.title}" by ${s.ownerName}? This cannot be undone.`)) return;
    try {
      await adminDeleteScript(s.id);
      if (open?.id === s.id) setOpen(null);
      void refresh();
    } catch (e) {
      onError(String((e as Error).message));
    }
  };

  return (
    <section className="card stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Script library ({scripts.length})</h3>
        <button onClick={() => void refresh()}>Refresh</button>
      </div>
      {scripts.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          No saved scripts yet. Producers save scripts to the cloud from their console.
        </p>
      )}
      {scripts.map((s) => (
        <div key={s.id} className="row" style={{ justifyContent: 'space-between' }}>
          <span>
            <strong>{s.title || 'Untitled'}</strong>{' '}
            <span className="muted">· {s.ownerName} · {s.blocks} blocks · {ago(s.updatedAt)}</span>
          </span>
          <span className="row" style={{ gap: '0.4rem' }}>
            <button onClick={() => void openScript(s.id)}>Open</button>
            <button className="danger" onClick={() => void remove(s)}>Delete</button>
          </span>
        </div>
      ))}

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(null)}>
          <div
            className="modal card stack"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 760, width: '92vw', maxHeight: '88vh', overflow: 'auto' }}
          >
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0 }}>
                {open.title || 'Untitled'}{' '}
                <span className="muted" style={{ fontWeight: 400 }}>· {open.ownerName}</span>
              </h3>
              <span className="row" style={{ gap: '0.5rem' }}>
                <span className="tag readout">
                  {saving === 'saving' ? 'Saving…' : saving === 'saved' ? 'Saved ✓' : 'Auto-saves'}
                </span>
                <button onClick={() => setOpen(null)} aria-label="Close">×</button>
              </span>
            </div>
            <ScriptTextEditor key={editorKey} project={open.project} onChange={save} minHeight={340} />
            <AiScriptPanel
              currentProject={open.project}
              onApply={(p) => {
                setOpen((cur) => (cur ? { ...cur, project: p } : cur));
                setEditorKey((k) => k + 1);
                void save(p);
              }}
              onError={onError}
            />
          </div>
        </div>
      )}
    </section>
  );
}
